// src/hooks/usePlayers.js
// Supplies the roster to the public pages.
//
// Reads from Firestore, but falls back to the original bundled roster if
// Firestore is empty or unreachable — so the site never renders an empty roster,
// even before the one-time import has been run.

import { useEffect, useMemo, useState } from "react";
import {
  subscribeToAllPlayers,
  isLive,
  isInGroup,
  effectiveAlsoAppearsIn,
  liveSince,
} from "../lib/playersService";
import staticPlayers from "../data/playersData";
import { LEGACY_PLAYER_IMAGES, LEGACY_IMAGE_POSITIONS } from "../data/legacyPlayerImages";
import { displayableImageUrl } from "../lib/imageUpload";

/**
 * Normalise a record so every page can rely on the same fields regardless of
 * whether it came from Firestore or the bundled file.
 *
 * `id` stays the human-readable slug because routes are /roster/:playerId and
 * the existing pages match on it. The Firestore document id is exposed
 * separately as `docId` for admin operations.
 */
function normalise(player, { fromFirestore }) {
  const slug = fromFirestore ? player.slug || player.name : player.id;
  // Imported players carry the name their bundled photo was filed under, so a
  // rename doesn't orphan the picture. Falls back to the current name for
  // records predating that field.
  const imageKey = player.legacyImageKey || player.name;

  return {
    ...player,
    id: slug,
    docId: fromFirestore ? player.id : null,
    // An uploaded photo always wins; otherwise use the bundled one.
    image: displayableImageUrl(player.imageUrl, player.imagePath) || LEGACY_PLAYER_IMAGES[imageKey] || null,
    imagePosition:
      player.imagePosition || LEGACY_IMAGE_POSITIONS[imageKey] || "center top",
    // Resolved here so every page sees the same answer, whether the player has
    // a stored value or is still falling back to their original listings.
    alsoAppearsIn: effectiveAlsoAppearsIn({ ...player, slug }),
  };
}

/**
 * The live roster.
 * Returns { players, loading, usingFallback }.
 */
export function usePlayers() {
  const [remote, setRemote] = useState(null); // null while loading
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const unsub = subscribeToAllPlayers(
      (list) => setRemote(list),
      (err) => {
        console.warn("Roster fetch failed, using bundled roster:", err);
        setFailed(true);
      }
    );
    return unsub;
  }, []);

  const usingFallback = failed || (remote !== null && remote.length === 0);

  const players = useMemo(() => {
    if (usingFallback) {
      return staticPlayers.map((p) => normalise(p, { fromFirestore: false }));
    }
    if (remote === null) return [];
    return remote
      .filter((p) => isLive(p))
      .map((p) => normalise(p, { fromFirestore: true }));
  }, [remote, usingFallback]);

  return {
    players,
    loading: remote === null && !failed,
    usingFallback,
  };
}

/**
 * Roster for one page, e.g. "cdl" or "warzone".
 * Includes players whose own division belongs to the group, plus anyone
 * cross-listed into it.
 */
export function usePlayersByGroup(group) {
  const { players, loading, usingFallback } = usePlayers();
  const filtered = useMemo(
    () => players.filter((p) => isInGroup(p, group)),
    [players, group]
  );
  return { players: filtered, loading, usingFallback };
}

/**
 * Players who went live on the site within the last `days`, newest first.
 *
 * Because this reads the same live roster as everything else, removing or
 * hiding someone drops them out of the announcement immediately, and members
 * age out on their own — nobody has to remember to clear the list.
 *
 * Timed from when a player became visible rather than when their record was
 * created, so drafting someone well before publishing doesn't eat their run.
 */
export function useNewPlayers(days = 14) {
  const { players, loading } = usePlayers();
  const cutoff = days * 24 * 60 * 60 * 1000;

  const recent = useMemo(() => {
    const now = Date.now();
    return players
      .filter((p) => !p.excludeFromNewMembers)
      // A graduate being added is someone leaving for the pro stage, not a new
      // arrival — they'd read very oddly under "Welcome, new members".
      .filter((p) => p.division !== "Graduates")
      .map((p) => ({ player: p, since: liveSince(p) }))
      .filter(({ since }) => since !== null && now - since <= cutoff)
      .sort((a, b) => b.since - a.since)
      .map(({ player }) => player);
  }, [players, cutoff]);

  return { players: recent, loading };
}
